import { Component, ComponentType } from '../../../../types';
import { BaseCommandHandler, CommandResult } from '../BaseCommandHandler';

interface ChecklistItem {
  id: string;
  label: string;
  checked: boolean;
  assignee?: string;
  dueDate?: string;
  priority?: 'critical' | 'high' | 'medium' | 'low';
  group?: string;
  notes?: string;
}

/**
 * Handles AI chat commands for CHECKLIST components.
 * Supports adding/removing items, toggling, priority, assignee, grouping, and progress display.
 */
class ChecklistCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Add item
    this.commandPatterns.set(
      /(?:add|create|new)\s+(?:item|task|todo)\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const newItem: ChecklistItem = {
          id: String(Date.now()),
          label: match[1].trim(),
          checked: false,
          priority: 'medium',
        };
        return { properties: { ...component.properties, items: [...items, newItem] } };
      }
    );

    // Remove item by name
    this.commandPatterns.set(
      /(?:remove|delete)\s+(?:item|task|todo)\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const label = match[1].trim().toLowerCase();
        const filtered = items.filter(i => i.label.toLowerCase() !== label);
        if (filtered.length === items.length) return null;
        return { properties: { ...component.properties, items: filtered } };
      }
    );

    // Check / uncheck item
    this.commandPatterns.set(
      /(?:check|complete|finish|mark)\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const label = match[1].trim().toLowerCase();
        const updated = items.map(i =>
          i.label.toLowerCase() === label ? { ...i, checked: true } : i
        );
        return { properties: { ...component.properties, items: updated } };
      }
    );

    this.commandPatterns.set(
      /uncheck\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const label = match[1].trim().toLowerCase();
        const updated = items.map(i =>
          i.label.toLowerCase() === label ? { ...i, checked: false } : i
        );
        return { properties: { ...component.properties, items: updated } };
      }
    );

    // Check all / uncheck all
    this.commandPatterns.set(
      /(?:check|complete|finish|mark)\s+all(?:\s+items)?\s*$/i,
      (_match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const updated = items.map(i => ({ ...i, checked: true }));
        return { properties: { ...component.properties, items: updated } };
      }
    );

    this.commandPatterns.set(
      /uncheck\s+all(?:\s+items)?\s*$/i,
      (_match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const updated = items.map(i => ({ ...i, checked: false }));
        return { properties: { ...component.properties, items: updated } };
      }
    );

    // Set priority
    this.commandPatterns.set(
      /set\s+priority\s+(?:of\s+)?["']?(.+?)["']?\s+to\s+(critical|high|medium|low)\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const label = match[1].trim().toLowerCase();
        const priority = match[2].toLowerCase() as ChecklistItem['priority'];
        const updated = items.map(i =>
          i.label.toLowerCase() === label ? { ...i, priority } : i
        );
        return { properties: { ...component.properties, items: updated } };
      }
    );

    // Assign item
    this.commandPatterns.set(
      /assign\s+["']?(.+?)["']?\s+to\s+["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const items = this.getItems(component);
        const label = match[1].trim().toLowerCase();
        const assignee = match[2].trim();
        const updated = items.map(i =>
          i.label.toLowerCase() === label ? { ...i, assignee } : i
        );
        return { properties: { ...component.properties, items: updated } };
      }
    );

    // Group by
    this.commandPatterns.set(
      /group\s+by\s+(priority|assignee|group|none)\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        const value = match[1].toLowerCase();
        return {
          properties: {
            ...component.properties,
            groupBy: value === 'none' ? undefined : value,
          },
        };
      }
    );

    // Show/hide progress
    this.commandPatterns.set(
      /(show|hide)\s+progress\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        return {
          properties: {
            ...component.properties,
            showProgress: match[1].toLowerCase() === 'show',
          },
        };
      }
    );

    // Set title
    this.commandPatterns.set(
      /(?:set\s+)?title\s+(?:to\s+)?["']?(.+?)["']?\s*$/i,
      (match: RegExpMatchArray, component: Component) => {
        return {
          properties: { ...component.properties, title: match[1].trim() },
        };
      }
    );
  }

  private getItems(component: Component): ChecklistItem[] {
    const props = component.properties as Record<string, unknown>;
    return (Array.isArray(props?.items) ? props.items : []) as ChecklistItem[];
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.CHECKLIST) return false;
    const keywords = [
      'add item', 'add task', 'add todo', 'new item', 'new task',
      'remove item', 'delete item', 'remove task', 'delete task',
      'check', 'uncheck', 'complete', 'finish', 'mark',
      'set priority', 'assign', 'group by',
      'show progress', 'hide progress', 'title',
    ];
    const lower = command.toLowerCase();
    return keywords.some(kw => lower.includes(kw));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const result = this.matchPatterns(command, component);
    if (!result) {
      return {
        success: false,
        command: { type: 'property', confidence: 0, description: 'Unrecognized checklist command' },
        suggestions: await this.getExampleCommands(),
      };
    }

    const validation = this.validateChanges(result, component);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
        suggestions: await this.getExampleCommands(),
      };
    }

    return {
      success: true,
      command: {
        type: 'property',
        changes: result,
        confidence: 0.9,
        description: `Checklist: ${command}`,
      },
      suggestions: [],
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'add item "Review PR"',
      'check "Review PR"',
      'uncheck "Review PR"',
      'check all items',
      'set priority of "Review PR" to high',
      'assign "Review PR" to Alice',
      'group by priority',
      'show progress',
      'hide progress',
      'title "Sprint Tasks"',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const items = this.getItems(component);
    const suggestions: string[] = ['add item "New task"'];
    const unchecked = items.find(i => !i.checked);
    if (unchecked) suggestions.push(`check "${unchecked.label}"`);
    const checked = items.find(i => i.checked);
    if (checked) suggestions.push(`uncheck "${checked.label}"`);
    if (items.length > 0) suggestions.push('check all items');
    suggestions.push('group by priority');
    return suggestions;
  }
}

export const checklistCommandHandler = new ChecklistCommandHandler();
